<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="false"
                title="Discharge Order"
                @onClickCloseButton="onClickCloseButton"
            />
        </div>

        <div class="d-flex justify-content-center mt-1">
            <img class="cover-photo" :src="companyInfo.cover_photo" alt="">
        </div>

        <div class="row note-section">
            <div class="col-md-12 col-12 col-lg-6 border-right">
                <UpperLeft
                    v-if="admissionInfo && diagnosisNotes"
                    :companyInfo="companyInfo"
                    :admissionInfo="admissionInfo"
                    :consultants="consultants"
                    :admittedByUsers="admittedByUsers"
                    :notes="diagnosisNotes"
                    v-model:dischargeDate="dischargeDate"
                    v-model:humanResource="admissionData.human_resource"
                    v-model:admittedBy="admissionData.admitted_by_user"
                />
            </div>

            <div class="col-md-12 col-12 col-lg-6 py-1">
                <h3>INVESTIGATIONS</h3>
                <UpperRight
                    v-if="investigationNotes.length"
                    v-for="(note, index) in investigationNotes"
                    :key="index"
                    :index="index"
                    :note="note"
                    @update:noteRemarks="updateRemarks"
                />
            </div>
        </div>

        <div class="horizontal-divider"></div>

        <div class="row note-section mt-0">
            <div class="col-md-12 col-lg-6 border-right py-2">
                <h3 class="px-2">ছুটিকালীন চিকিৎসা (TREATMENT ON DISCHARGE)</h3>
                <RxNote
                    class="discharge-rx"
                    :note="dischargeNotes.rx"
                    v-if="dischargeNotes.rx"
                    @openModal="openModal"
                    :medicineNotes="medicineNotes"
                />
            </div>

            <div class="col-md-12 col-lg-6 py-2">
                <h3 class="px-2">উপদেশ</h3>
                <AdmissionNote
                    class="discharge-rx"
                    :note="dischargeNotes.advice"
                    v-if="dischargeNotes.advice"
                    @openModal="openModal"
                />

                <div class="row mb-1 note-section p-1">
                    <div class="col-lg-6">
                        <h4>Discharge Composed By</h4>
                    </div>
                    <div class="col-lg-6">
                        <AsyncSelect 
                            placeholder="Select Discharge Composed By"
                            v-model="composedByDoctor"
                            :api-service="fetchServiceList"
                            :additional-query="{ resource_type: 'human_resources' }"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-center">
            <button @click="submit" class="btn btn-primary m-2">
                Save
            </button>
        </div>

        <AddNoteModal
            v-if="store.state.isModalOpen"
            :note="singleNote"
        />

        <AddRxNoteModal
            v-if="store.state.isModalOpenTwo"
            :note="singleNote"
            :products="products"
        />

        <Loader v-if="loader"/>

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import UpperLeft from '@/components/molecule/company/hospital/cag-pci/discharge/UpperLeft.vue';
import UpperRight from '@/components/molecule/company/hospital/cag-pci/discharge/UpperRight.vue';
import handleContact from '@/services/modules/contact';
import AdmissionNote from '@/components/molecule/company/hospital/note/Note.vue';
import RxNote from '@/components/molecule/company/hospital/note/RxNote.vue';
import AddNoteModal from '@/components/molecule/company/hospital/note/AddNoteModal.vue';
import AddRxNoteModal from '@/components/molecule/company/hospital/note/AddRxNoteModal.vue';
import handleHospital from '@/services/modules/hospital';
import handleCompany from "@/services/modules/company";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Loader from '@/components/atom/LoaderComponent';
import cagDischargePdfHelper from "@/services/utils/pdf/cagDischargePdfHelper.js";

import { computed, inject, onMounted, reactive, ref } from 'vue';
import { head } from 'lodash';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { generateUUID, json_string_parse, formatDateToDdMmYy, json_string, getCurrentDateTime, groupBy, pluck } from '@/services/utils/global';

const { fetchSingleDischargeOrder, storePrescription, 
        updatePrescription, fetchDischargeStatus, 
        fetchServiceList, fetchSingleNote, 
        fetchGroupTemplate } = handleHospital();
const { fetchCompanyInfo } = handleCompany();
const { fetchContactProfiles } = handleContact();
const { generatePdf } = cagDischargePdfHelper();

const singleNote = ref(null);
const store = useStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const loader = ref(false);
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const medicineNotes = reactive([]);
const service = ref(null);
const companyInfo = ref({});
const treatedByDoctors = ref('');
const serviceDoctorOrder = ref({});
const authUserServiceResourceId = ref(null);
const admissionInfo = ref({});
const consultants = reactive([]);
const admittedByUsers = reactive([]);
const isReleased = ref(false);
const investigationNotes = ref([]);
const diagnosisNotes = ref([]);
const diagnosisKey = ref({})

const dischargeNotes = reactive({
    discharge_note: null,
    rx: null,
    follow_up: null,
    investigation: null,
    advice: null
})

const newInvestigations = ref([]);

const dischargeDate = ref(getCurrentDateTime());
const composedByDoctor = ref(null);
const formData = reactive({
    suffering_from: null,
    mode_of_discharge: null,
})
const dischargeModes = reactive([])

const admissionData = reactive({
    human_resource: null,
    admitted_by_user: null
})

const submit = async () => {
    const formData = formatFormData();
    const doctorOrderId = service.value.doctor_order && service.value.doctor_order.id;
    const prescriptionAction = doctorOrderId ? updatePrescription : storePrescription

    loading.value = true
    await prescriptionAction(formData, doctorOrderId).then((res) => {
        loading.value = false
        if (!res.status) return showError(res.message);
        onClickToClose();
        goToPrint();
        return showSuccess(res.message);
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const goToPrint = () => {
    generatePdf(admissionInfo.value, route.params.serviceId, route.params.companyId)
}

const onClickToClose = () => {
    router.push({name: 'admission-discharge',
        params: route.params,
        query: route.query
    })
}

const formatFormData = () => {
    const {rx, investigation, other_notes} = formatNotes();
    const formattedData = {
        rx_notes: rx,
        investigations: investigationNotes.value,
        other_notes: other_notes,
        contact_profile_id: service.value.contact_profile_id,
        service_general_id: service.value.id,
        service_resource_id: authUserServiceResourceId.value,
        company_id: route.params.companyId,
        prescription_type: 'discharge_order',
        additional_info: json_string(formData),
        delivery_date_time: dischargeDate.value,
        human_resource_id: admissionData.human_resource ? admissionData.human_resource.id : null,
        admitted_by: admissionData.admitted_by_user.id,
        discharge_type: 'cag_pci'
    }

    if(composedByDoctor.value) {
        formattedData.composed_by = composedByDoctor.value.id;
    }

    return formattedData;
}

const formatNotes = () => {
    const noteObj = {
        rx: [],
        investigation: [],
        other_notes: [],
    };

    const allNotes = Object.values(dischargeNotes).filter(item => item.selectedNotes.length);
    for(const note of allNotes){
        if(noteObj.hasOwnProperty(note.value)){
          const formattedSelectedNotes = note.selectedNotes.map(item => {
            return {
              ...item,
              service_resource_id: authUserServiceResourceId.value
            };
          });

          noteObj[note.value] = noteObj[note.value].concat(formattedSelectedNotes);
          continue;
        }
        noteObj['other_notes'].push({
            id: note.id ? note.id : null,
            type : note.value,
            note : JSON.stringify(note.selectedNotes),
            new_notes: note.newNotesByDoctor
        });
    }
    return noteObj;
}

const openModal = ({note, index}) => {
    singleNote.value = null;
    singleNote.value = {
        index: index,
        name: note.name,
        value: note.value,
        selectedNotes: note.selectedNotes,
        preAddedNotes: json_string_parse(note.preAddedNotes),
        noteMasters: note.noteMasters,
        newNotesByDoctor: note.newNotesByDoctor
    }

    if(['rx', 'investigation'].includes(note.value)){
        store.state.isModalOpenTwo = true;
        return;
    }
    
    store.state.isModalOpen = true;
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&admission_id=${route.params.admissionId}`;
    query += `&discharge_type=cag_pci`;
    return query;
}

const setNoteData = (notes) => {
    for(let note of notes){
        dischargeNotes[note.value] = note;
    }
}

const setEditData = () => {
    if(service.value.delivery_date_time){
        dischargeDate.value = getCurrentDateTime(service.value.delivery_date_time);
    }

    const investigationHistoryNote = head(service.value.ipd_register.notes)
    if(investigationHistoryNote){
        newInvestigations.value = newInvestigations.value.concat(investigationHistoryNote.note)
    }

    const doctorOrder = service.value.doctor_order;
    if(!doctorOrder) return;

    serviceDoctorOrder.value = doctorOrder;

    if(doctorOrder.composed_by){
        composedByDoctor.value = {
            id: doctorOrder.composed_by,
            name: doctorOrder.composed_service_name
        }
    }

    if(doctorOrder.additional_info){
        setFormData(doctorOrder.additional_info);
    }

    if(doctorOrder.medicine_orders.length){
        setMedicineOrderData(doctorOrder.medicine_orders, 'rx');
    }

    if(doctorOrder.notes.length){
        setOtherNoteData(doctorOrder.notes);
    }
}

const setFormData = (additionalInfo) => {
    formData.mode_of_discharge = additionalInfo.mode_of_discharge;
    formData.suffering_from = additionalInfo.suffering_from
}

const setOtherNoteData = (other_notes) => {
    for(let noteItem of other_notes){

        let prevNote = dischargeNotes[noteItem.type]
        if(!prevNote) continue;

        prevNote.id = noteItem.id;
        prevNote.selectedNotes.push(...noteItem.note);
    }
}

const setMedicineOrderData = (medicineRx, noteType) => {

    for(let note of medicineRx){

        const noteObj = {
            id: note.id,
            product_id: note.product_id,
            name: note.product.name,
            remarks: note.remarks,
            is_selected: true,
        };

        if(noteType === 'rx'){
            noteObj['dose'] = note.dose;
            noteObj['unit'] = note.unit;
            noteObj['duration'] = note.duration;
            noteObj['madication_status'] = note.madication_status;
        }
        dischargeNotes[noteType].selectedNotes.push(noteObj)
    }
}

const fetchInitialData = async () => {
    const query = getQuery();
    const addQuery = `?company_id=${route.params.companyId}&search_type=all&type=cag`;
    const investemplateQuery = `?company_id=${route.params.companyId}&template_type=investigation`;

    loader.value = true;

    try {
        // Step 1: First wait for discharge order
        const dischargeRes = await fetchSingleDischargeOrder(query, route.params.serviceId, 'discharge_order');
        if (!dischargeRes.status) return showError(dischargeRes.message);

        isReleased.value = !!dischargeRes.data.service.doctor_order;
        service.value = dischargeRes.data.service;
        newInvestigations.value = dischargeRes.data.investigations;
        setNoteData(dischargeRes.data.dischargeNotes);
        if(isReleased.value) {
            setInvestigationUpdate(dischargeRes.data.service.doctor_order);
        }
        medicineNotes.push(...dischargeRes.data.rxNotes);
        dischargeModes.push(...dischargeRes.data.dischargeModes);
        treatedByDoctors.value = dischargeRes.data.treated_by_doctors;
        authUserServiceResourceId.value = dischargeRes.data.auth_user_service_resource_id;
        diagnosisKey.value = dischargeRes.data.dischargeNotes.find(i => i.value === 'diagnosis');

        // Step 2: Now load other promises in parallel
        const promises = [
            fetchSingleNote(addQuery, route.params.admissionId).then((res) => {
                admissionInfo.value = res.data;
                admissionData.admitted_by_user = {
                    id: res.data.admitted_by_user.id,
                    name: res.data.admitted_by_user.full_name
                }
                admissionData.human_resource = res.data.human_resource
                setDiagnosisNote(admissionInfo.value.notes)
            }),

            fetchCompanyInfo(route.params.companyId).then((res) => {
                companyInfo.value = res.data;
            }),

            fetchServiceList(query, 'human_resources').then((res) => {
                if (res.status) consultants.push(...res.data);
            }),

            fetchContactProfiles(query + `&type=doctor`).then((res) => {
                if (res.status) admittedByUsers.push(...res.data);
            })
        ];

        if (!isReleased.value) {
            promises.push(
                fetchGroupTemplate(investemplateQuery).then((res) => {
                    if (! res.status) return 
                    setInvestigationNote(res.data.data[0]);
                }),
            );
        }

        await Promise.all(promises);
        setEditData();
    } catch (err) {
        console.error(err);
    } finally {
        loader.value = false;
    }
};

const setDiagnosisNote = (data) => {
    const notes = data;
    const noteName = diagnosisKey.value.name;

    diagnosisNotes.value = notes
    .filter(note => Object.keys(noteName).includes(note.type))
    .map(note => ({
      ...note,
      name: noteName[note.type]
    }));
};

const setInvestigationUpdate = (doctorOrder) => {
    const mappedNotes = doctorOrder.investigation_orders.map(t => ({
        id: t.id,
        product_id: t.product_id,
        name: t.product.name || '',
        remarks: t.remarks,
        is_selected: true,
        service_resource_id: authUserServiceResourceId.value
    }));

    investigationNotes.value = [...mappedNotes];
}

const setInvestigationNote = (invesTemplate) => {
    const mappedNotes = invesTemplate.templates.map(t => ({
        product_id: t.templateable_id,
        name: t.templateable.name || '',
        price: 0,
        remarks: "",
        is_selected: true,
        madication_status: "",
        status: t.status,
        order_date: "",
        service_resource_id: authUserServiceResourceId
    }));

    investigationNotes.value = [ ...mappedNotes  ];
}

const updateRemarks = ({ index, remarks }) => {
    investigationNotes.value[index].remarks = remarks;
};

const onClickCloseButton = () => {
    router.push({name: 'admission-discharge',
        params: route.params,
        query: route.query,
    })
}

onMounted(() => {
    fetchInitialData();
})

</script>

<style scoped>
.discharge-rx .row {
    padding: 0 2% !important;
}
.discharge-rx {
    width: 95%;
    margin: auto;
    margin-bottom: 2%;
}

.cover-photo {
    width: 100%;
    object-fit: cover;
}

.horizontal-divider {
    width: 96%;
    border-top: 2px solid #ccc;
    margin-left: auto;
    margin-right: auto;
}

.border-right {
    border-right: 2px solid #ccc;
}

.note-section {
    margin-top: 2%;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    width: 96%;
    margin-left: 2%;
}
</style>